import { DownOutlined, ImportOutlined, UserOutlined, SettingOutlined } from '@ant-design/icons';
import { Avatar, Drawer, Dropdown, Modal, Space, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/auth';
import Profile from '../../pages/Profile';
import { useDirty } from '../Blocker/dirty';
import { Header, UserInfo, UserMenuTrigger, VersionText } from './styles';

const { Text } = Typography;

const HeaderNavbar = () => {
  const { isDirty, setClean } = useDirty();
  const { t } = useTranslation();
  const { signOut, user } = useAuth();
  const [visibleShowDrawer, setVisibleDrawer] = useState(false);
  const [tenant] = useState(localStorage.getItem('@Auth:tenant'));
  const navigate = useNavigate();

  const tryNavigate = (path) => {
    if (isDirty) {
      Modal.confirm({
        content: t('Prompt.Leave'),
        onOk: () => {
          setClean();
          navigate(path);
        },
      });
    } else {
      navigate(path);
    }
  };

  const handleMenuClick = ({ key }) => {
    switch (key) {
      case 'change-password':
        setVisibleDrawer(true);
        break;
      case 'logout':
        signOut();
        break;
      default:
        break;
    }
  };

  const items = [
    {
      type: 'group',
      label: (
        <UserInfo>
          <Avatar size={48} style={{ backgroundColor: '#4085c6', flexShrink: 0 }}>
            {user?.userName?.[0]?.toUpperCase()}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ display: 'block', fontSize: '15px', marginBottom: '4px' }}>
              {user?.name}
            </Text>
            <Text type="secondary" style={{ fontSize: '13px', display: 'block' }}>
              {tenant || '-'}
            </Text>
          </div>
        </UserInfo>
      ),
      key: 'user-header',
    },
    { type: 'divider' },
    {
      label: (
        <div style={{ paddingLeft: '8px' }}>
          <Space>
            <UserOutlined />
            <span style={{ fontSize: '13px' }}>{t('User.User.Location')}</span>
          </Space>
          <div style={{ marginTop: '4px', paddingLeft: '24px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {user?.locationName || '-'}
            </Text>
          </div>
        </div>
      ),
      key: 'location',
      disabled: true,
    },
    {
      label: (
        <div style={{ paddingLeft: '8px' }}>
          <Space>
            <SettingOutlined />
            <span style={{ fontSize: '13px' }}>{t('Login.Tenant')}</span>
          </Space>
          <div style={{ marginTop: '4px', paddingLeft: '24px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {tenant || '-'}
            </Text>
          </div>
        </div>
      ),
      key: 'tenant',
      disabled: true,
    },
    { type: 'divider' },
    {
      label: (
        <Space>
          <SettingOutlined />
          <span>{t('Header.Modal.ChangePassword')}</span>
        </Space>
      ),
      key: 'change-password',
    },
    {
      label: (
        <Space>
          <ImportOutlined />
          <span>{t('Header.Modal.Logout')}</span>
        </Space>
      ),
      key: 'logout',
      danger: true,
    },
    { type: 'divider' },
    {
      label: (
        <VersionText>
          flowy {process.env.REACT_APP_BUILD_VERSION}
        </VersionText>
      ),
      key: 'version',
      disabled: true,
    },
  ];

  return (
    <>
      <Header className="font-face-gbook">
        <Dropdown
          trigger={['click']}
          menu={{ items, onClick: handleMenuClick }}
          placement="bottomRight"
          overlayStyle={{ minWidth: '280px' }}
        >
          <UserMenuTrigger onClick={(e) => e.preventDefault()}>
            <Avatar 
              size={32} 
              style={{ 
                backgroundColor: '#4085c6',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: '2px solid transparent',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.borderColor = '#4085c6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              {user?.userName?.[0]?.toUpperCase()}
            </Avatar>
            <div style={{ 
              marginLeft: '8px', 
              minWidth: 0, 
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              <Text 
                strong 
                style={{ 
                  fontSize: '14px', 
                  lineHeight: '1.2', 
                  color: '#000',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {user?.userName}
              </Text>
              <Text 
                type="secondary" 
                style={{ 
                  fontSize: '12px', 
                  lineHeight: '1.2',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {tenant || '-'}
              </Text>
            </div>
            <DownOutlined style={{ 
              marginLeft: '8px', 
              fontSize: '12px', 
              color: '#8c8c8c', 
              transition: 'transform 0.2s',
              flexShrink: 0
            }} />
          </UserMenuTrigger>
        </Dropdown>
      </Header>

      <Drawer
        title={
          <Space>
            <Avatar size={40} style={{ backgroundColor: '#4085c6' }}>
              {user?.userName?.[0]?.toUpperCase()}
            </Avatar>
            <div>
              <Text strong style={{ display: 'block', fontSize: '16px' }}>
                {t('Header.Modal.ChangePassword')}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {user?.userName}
              </Text>
            </div>
          </Space>
        }
        placement="right"
        closable={true}
        onClose={() => setVisibleDrawer(false)}
        open={visibleShowDrawer}
        width={480}
        styles={{
          header: {
            padding: '20px 24px',
            borderBottom: '1px solid #f0f0f0',
          },
          body: {
            padding: '24px',
          },
        }}
      >
        <Profile />
      </Drawer>
    </>
  );
};

export default HeaderNavbar;
